// components/SubscriptionDialog.tsx
import React from "react";
import { Button } from "@mui/material";
import { ModalWrapper } from "src/shared/components/ModalWrapper";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

interface SubscriptionDialogProps {
  open: boolean;
  message?: React.ReactNode;
  isExpired?: boolean;
  onClose: () => void;
  onRenew?: () => void;
}

const SubscriptionDialog: React.FC<SubscriptionDialogProps> = ({ open, message, isExpired = false, onClose, onRenew }) => {
  const defaultExpiredMessage = "Your subscription has expired. Please renew your plan to continue using all features without interruption.";
  const defaultGraceMessage = "Your subscription is about to expire. Please renew soon to avoid service disruption.";

  if (!open) return null;

  return (
    <ModalWrapper
      title={isExpired ? "Subscription Expired" : "Subscription Notice"}
      onClose={onClose}
      showCloseButton={!isExpired}
      showBackButton={false}
      width="max-w-2xl"
      disableClose={isExpired}
      positionClass="inset-0"
      // useTheme={false}
    >
      {/* Body Content */}
      <div className="px-6 py-4">
        {/* Message */}
        <div className="text-center space-y-3">
          <div className="flex items-start justify-center gap-2">
            <WarningAmberIcon sx={{ color: "#facc15", fontSize: 28 }} />

            <p className={`text-base leading-relaxed ${isExpired ? "text-gray-800 font-medium" : "text-gray-700"}`}>{message || (isExpired ? defaultExpiredMessage : defaultGraceMessage)}</p>
          </div>{" "}
        </div>

        {/* Contact Support Box - Enhanced styling */}
        <div className="mt-6 bg-gradient-to-br from-gray-50 to-gray-100/50 border border-gray-200 rounded-2xl p-5">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0"></div>
            <div className="flex-1">
              <p className="font-semibold text-gray-800">Need Assistance?</p>
              <p className="text-sm text-gray-600 mt-1 leading-relaxed">Our support team is here to help with any questions about your subscription.</p>
              <div className="mt-3 text-sm">
                <div className="flex items-center space-x-2 text-gray-600 whitespace-nowrap">
                  {/* Phone */}
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>

                  <a href="tel:+919876543210" className="hover:text-blue-600">
                    +91 98765 43210
                  </a>

                  <span className="text-gray-400">|</span>

                  {/* Email */}
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>

                  <a href="mailto:customersupport@imetanic.co" className="hover:text-blue-600 underline">
                  customersupport@imetanic.co
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons - Aligned to the right */}
        <div className="flex justify-end mt-6">
          <Button onClick={onClose} variant="outlined" className="!px-6 !py-2 !rounded-lg !normal-case !text-white !bg-blue-500 !border-gray-300">
            Later
          </Button>
        </div>
      </div>
    </ModalWrapper>
  );
};

export default SubscriptionDialog;
